<template>
  <div class="expenses-actions">
    <div class="left">
      <BulkActionDropdown
        v-if="showBulkAction"
        :menu-items="[
          {
            icon: 'check',
            label: this.$t('expenses.listing.labels.approveExpenses'),
            clickaction: 'APPROVE'
          },
          {
            icon: 'times',
            label: this.$t('expenses.listing.labels.rejectExpenses'),
            clickaction: 'REJECT'
          },
          { icon: 'copy', label: this.$t('expenses.listing.labels.bulkPdf'), clickaction: 'PDF' }
        ]"
        @bulk-action-selected="onBulkActionSelected($event)"
        @all-none-selected="selectOrDeselectExpenses($event)"
      />
      <div class="listing-title">{{ $t(titleAllOrFiltered) }}</div>
    </div>
    <div class="expenses-search">
      <div class="search">
        <tol-type-ahead
          :search-params="{
            url: `/expenses`,
            searchKeys: ['searchParam'],
            urlParams: {
              sortBy: '',
              sortDirection: '',
              recordsPerPage: ExpensesPerPage,
              offset: ExpensesListingOffset
            }
          }"
          :results-in-dropdown="false"
          :allow-empty-search="true"
          @filtered="onFiltered($event)"
        >
        </tol-type-ahead>
      </div>
    </div>
    <div class="right">
      <TolAltButton
        :tooltip="$t('expenses.listing.actions.exportToXlsx')"
        label="Export to excel"
        icon-name="file-excel"
        @click="exportAsExcel"
      />
      <ConfirmModal
        v-if="showConfirmModal"
        :title="selectedBulkAction"
        :action="selectedBulkAction"
        @continue-action="continueBulkAction($event)"
        @cancel-action="cancelBulkAction()"
      >
        <template v-slot:modalData
          >{{ $t('bulk.labels.totalSelected') }}: {{ selectedItems.length }}</template
        >
        <template v-slot:modalMessage>
          {{ $t('bulk.dialogs.approveRejectConfirmationDialog.confirmMessage', { message }) }}
        </template>
      </ConfirmModal>
      <VueHtml2pdf
        ref="ExpenseBatchDownloadComp"
        :show-layout="false"
        :paginate-elements-by-height="1400"
        pdf-format="a4"
        :manual-pagination="false"
        :enable-download="false"
        :preview-modal="true"
        :filename="`Expense-Batch`"
      >
        <section slot="pdf-content">
          <ExpensesBatchPDF @readyToExport="exportAsPdf" />
        </section>
      </VueHtml2pdf>
    </div>
  </div>
</template>

<script>
import TolAltButton from '@/app/components/TolAltButton';

import BulkActionDropdown from '@/app/components/BulkActionDropdown.vue';
import ConfirmModal from '@/app/components/ConfirmModal.vue';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import VueHtml2pdf from 'vue-html2pdf';
import {
  ExpensesListingActions,
  ExpensesListingGetters
} from '@/app/features/expenses/features/expenses-listing/expenses-listing.keys';
import { mapGetters } from 'vuex';
import ExpensesBatchPDF from '../../expense-details/components/ExpensesBatchPDF.vue';

export default {
  name: 'ExpensesActions',
  components: {
    TolAltButton,
    TolTypeAhead,
    ConfirmModal,
    BulkActionDropdown,
    VueHtml2pdf,
    ExpensesBatchPDF
  },
  props: {
    titleAllOrFiltered: {
      type: String,
      default: 'expenses.listing.actions.titleAll'
    },
    showBulkAction: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showConfirmModal: false,
      selectedBulkAction: ''
    };
  },
  computed: {
    ...mapGetters([
      ExpensesListingGetters.ExpensesPerPage,
      ExpensesListingGetters.ExpensesFilteredSortedRecordCount,
      ExpensesListingGetters.ExpensesListingOffset,
      ExpensesListingGetters.ExpensesSearchParam
    ]),
    isApprover: {
      get() {
        return this.$store.state.currentUser.isApprover;
      }
    },
    anyItemsSelected() {
      return this.$store.getters.SelectedExpenses('referenceNumber').length > 0;
    },
    selectedItems() {
      return this.$store.getters.SelectedExpenses('referenceNumber');
    },
    message: {
      get() {
        let res = '';
        switch (this.selectedBulkAction) {
          case 'APPROVE':
            res = this.$t('bulk.actions.approve');
            break;
          case 'REJECT':
            res = this.$t('bulk.actions.reject');
            break;
          case 'PDF':
            res = this.$t('bulk.actions.saveAsPdf');
            break;
        }
        return res;
      }
    }
  },
  methods: {
    onFiltered(results) {
      let searchedExpenses = results.data;
      let searchKey = results.searchParam;
      this.$store.dispatch(ExpensesListingActions.UpdateExpensesSearchParam, {
        expenses: searchedExpenses,
        searchParam: searchKey
      });
    },
    exportAsExcel() {
      this.$store.dispatch(ExpensesListingActions.ExportExpensesAsExcel);
    },
    onBulkActionSelected(bulkAction) {
      this.selectedBulkAction = bulkAction;
      if (this.canDoBulkAction(bulkAction)) this.showConfirmModal = this.anyItemsSelected;
      else {
        this.$store.dispatch(TolErrorsActions.SetError, {
          message: this.$t('bulk.errors.approveRejectDisallowed')
        });
      }
      this.$store.dispatch(ExpensesListingActions.SetExpensesBulkAction, bulkAction);
    },
    continueBulkAction(event) {
      this.showConfirmModal = false;
      this.$store.dispatch(ExpensesListingActions.DoExpensesBulkAction, event);
    },
    cancelBulkAction() {
      this.showConfirmModal = false;
      this.$store.dispatch(ExpensesListingActions.SelectDeselectAllExpenses, {
        clicked: false
      });
    },
    selectOrDeselectExpenses(event) {
      this.$store.dispatch(ExpensesListingActions.SelectDeselectAllExpenses, event);
    },
    canDoBulkAction(bulkAction) {
      const selectedExpenseStatuses = this.$store.getters.SelectedExpenses('status');
      switch (bulkAction) {
        case 'APPROVE':
          return selectedExpenseStatuses.every(
            status => status === 'SUBMITTED' || status === 'REJECTED'
          );
        case 'REJECT':
          return selectedExpenseStatuses.every(
            status => status === 'SUBMITTED' || status === 'APPROVED'
          );
        case 'PDF':
          return true;
        default:
          return false;
      }
    },
    exportAsPdf() {
      this.$refs.ExpenseBatchDownloadComp.generatePdf();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../../styles/colors.scss';
@import '../../../../../styles/variables.scss';

.expenses-actions {
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  background-color: $background-level-2;
  padding: 60px;
  .left {
    flex-basis: 20%;
    display: flex;
  }

  .right {
    display: flex;
    flex-basis: 20%;
    margin-left: auto;
    justify-content: flex-end;
  }

  .listing-title {
    color: $text-color-dark;
    font-size: 21px;
    font-weight: bold;
    letter-spacing: -0.24px;
    line-height: 28px;
    padding-top: 10px;
  }

  .expenses-search {
    display: flex;
    flex-basis: 60%;
    background-color: $background-level-2;

    .search {
      padding-top: 0px;
      width: 100%;
    }
  }
}
</style>
